import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Injectable,
	Input,
	LOCALE_ID,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import {
	CalendarUtils,
	CalendarWeekViewComponent,
	DateAdapter,
	getWeekViewPeriod
} from 'angular-calendar';
import {
	WeekView,
	GetWeekViewArgs,
	WeekViewTimeEvent,
	CalendarEvent,
	WeekViewAllDayEvent
} from 'calendar-utils';
import { DragEndEvent, DragMoveEvent } from 'angular-draggable-droppable';
import { Employee } from 'src/app/shared/models/employee/employee';
import { ConvertObjectToCalendarEvent } from 'src/app/shared/utils/convert-object-to-calendar-event';
import { FacadeService } from 'src/app/core/services/facade.service';
import { DateUtils } from 'src/app/shared/utils/date-utils';

interface WeekViewAligned extends WeekView {
	employees: Employee[];
}

interface GetWeekViewArgsWithUsers extends GetWeekViewArgs {
	employees: Employee[];
}

@Injectable()
export class WeekViewAlignedCalendarUtils extends CalendarUtils {
	getWeekView(args: GetWeekViewArgsWithUsers): WeekViewAligned {
		const { period } = super.getWeekView(args);
		const view: WeekViewAligned = {
			period,
			allDayEventRows: [],
			hourColumns: [],
			employees: [...args.employees]
		};

		view.employees.forEach((employee, columnIndex) => {
			let events = args.events.filter((event) => {
				return event.meta?.employee?.id === employee.id;
			});
			const columnView = super.getWeekView({
				...args,
				events
			});
			view.hourColumns.push(columnView.hourColumns[0]);
			columnView?.allDayEventRows.forEach(({ row }, rowIndex) => {
				view.allDayEventRows[rowIndex] = view?.allDayEventRows[rowIndex] || {
					row: []
				};
				view.allDayEventRows[rowIndex].row.push({
					...row[0],
					offset: columnIndex,
					span: 1
				});
			});
		});

		return view;
	}
}

@Component({
	selector: 'app-week-view-aligned',
	templateUrl: 'week-view-aligned.component.html',
	styleUrls: ['./week-view-aligned.component.scss'],
	providers: [WeekViewAlignedCalendarUtils]
})
export class WeekViewAlignedComponent
	extends CalendarWeekViewComponent
	implements OnChanges, OnInit
{
	@Input() selectedCalendarDate: Date;
	@Input() employees: Employee[] = [];
	@Output() getEmployeeIdOfClickedColumnOutput: EventEmitter<any> =
		new EventEmitter();

	view: WeekViewAligned;

	daysInWeek = 1;

	isMonday = false;

	// employeesFiltered;

	today = new Date();

	constructor(
		protected cdr: ChangeDetectorRef,
		protected utils: WeekViewAlignedCalendarUtils,
		@Inject(LOCALE_ID) locale: string,
		protected dateAdapter: DateAdapter,
		protected element: ElementRef<HTMLElement>,
		private convertObjectToCalEv: ConvertObjectToCalendarEvent,
		private facade: FacadeService
	) {
		super(cdr, utils, locale, dateAdapter, element);
	}

	trackByEmployeeId = (index: number, row: Employee) => row.id;

	ngOnChanges(changes: SimpleChanges): void {
		this.isMonday = this.viewDate.getDay() === 1;
		super.ngOnChanges(changes);
		if (changes.employees) {
			this.refreshData();
		}
	}

	refreshData() {
		this.refreshBody();
		this.emitBeforeViewRender();
	}

	getDayColumnWidth(eventRowContainer: HTMLElement): number {
		return Math.floor(eventRowContainer.offsetWidth / this.employees.length);
	}

	getEmployeeIdOfClickedColumn(segment, segmentElement) {
		const emp: Employee = segmentElement.employees[segmentElement.i];

		this.getEmployeeIdOfClickedColumnOutput.emit({
			segment,
			employeeId: emp.id
		});
	}

	dragMove(dayEvent: WeekViewTimeEvent, dragEvent: DragMoveEvent) {
		if (this.snapDraggedEvents) {
			const newEmployee = this.getDraggedEmployeeColumn(dayEvent, dragEvent.x);
			const newEventTimes = this.getDragMovedEventTimes(
				dayEvent,
				{ ...dragEvent, x: 0 },
				this.dayColumnWidth,
				true
			);
			const originalEvent = dayEvent.event;
			const adjustedEvent = {
				...originalEvent,
				...newEventTimes,
				meta: { ...originalEvent.meta, employee: newEmployee }
			};
			const tempEvents = this.events.map((event) => {
				if (event === originalEvent) {
					return adjustedEvent;
				}
				return event;
			});
			this.restoreOriginalEvents(
				tempEvents,
				new Map([[adjustedEvent, originalEvent]])
			);
		}
		this.dragAlreadyMoved = true;
	}

	dragEnded(
		weekEvent: WeekViewAllDayEvent | WeekViewTimeEvent,
		dragEndEvent: DragEndEvent,
		dayWidth: number,
		useY = false
	) {
		super.dragEnded(
			weekEvent,
			{
				...dragEndEvent,
				x: 0
			},
			dayWidth,
			useY
		);
		this.getDraggedEmployeeColumn(weekEvent, dragEndEvent.x);
	}

	protected getWeekView(events: CalendarEvent[]) {
		return this.utils.getWeekView({
			events,
			employees: this.employees,
			viewDate: this.viewDate,
			weekStartsOn: this.weekStartsOn,
			excluded: this.excludeDays,
			precision: this.precision,
			absolutePositionedEvents: true,
			hourSegments: this.hourSegments,
			dayStart: {
				hour: this.dayStartHour,
				minute: this.dayStartMinute
			},
			dayEnd: {
				hour: this.dayEndHour,
				minute: this.dayEndMinute
			},
			segmentHeight: this.hourSegmentHeight,
			weekendDays: this.weekendDays,
			...getWeekViewPeriod(
				this.dateAdapter,
				this.viewDate,
				this.weekStartsOn,
				this.excludeDays,
				this.daysInWeek
			)
		});
	}

	getDraggedEmployeeColumn(
		dayEvent: WeekViewTimeEvent | WeekViewAllDayEvent,
		xPixels: number
	) {
		const columnsMoved = Math.round(xPixels / this.dayColumnWidth);
		const currentColumnIndex = this.view.employees.findIndex(
			(employee) => employee === dayEvent.event.meta.employee
		);
		const newIndex = currentColumnIndex + columnsMoved;
		return this.view.employees[newIndex];
	}
}
